import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Card } from "@/components/dash";
import CoinIcon from "@/components/CoinIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import api, { apiErr } from "@/lib/api";
import { formatTRY } from "@/lib/format";

const COINS = ["BTC", "ETH", "USDT", "USDC", "SOL"];

export default function CustomCheckout() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/checkout-settings").then(({ data }) => setS(data)).catch(() => setS({})); }, []);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/checkout-settings", s);
      setS(data);
      toast.success("Checkout ayarları kaydedildi.");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const toggleCoin = (c) => {
    const list = s.active_coins || [];
    set("active_coins", list.includes(c) ? list.filter((x) => x !== c) : [...list, c]);
  };

  if (!s) return <Spinner />;

  return (
    <div>
      <PageHeader title="Checkout Özelleştirme" desc="Ödeme sayfanızı markanıza göre tasarlayın."
        action={<Button onClick={save} disabled={saving} data-testid="save-checkout-btn" className="rounded-full">{saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={16} /> Kaydet</>}</Button>}
      />
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 space-y-4" data-testid="checkout-editor">
          <div><Label className="text-sm">Mağaza Adı</Label><Input value={s.store_name || ""} onChange={(e) => set("store_name", e.target.value)} className="mt-1.5" /></div>
          <div><Label className="text-sm">Logo URL</Label><Input value={s.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} className="mt-1.5" placeholder="https://..." /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-sm">Ana Renk</Label><div className="mt-1.5 flex gap-2"><input type="color" value={s.primary_color || "#0066FF"} onChange={(e) => set("primary_color", e.target.value)} className="h-9 w-12 rounded border border-border" /><Input value={s.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)} /></div></div>
            <div><Label className="text-sm">Buton Rengi</Label><div className="mt-1.5 flex gap-2"><input type="color" value={s.button_color || "#0066FF"} onChange={(e) => set("button_color", e.target.value)} className="h-9 w-12 rounded border border-border" /><Input value={s.button_color || ""} onChange={(e) => set("button_color", e.target.value)} /></div></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-sm">Yazı Tipi</Label><Input value={s.font_family || "Inter"} onChange={(e) => set("font_family", e.target.value)} className="mt-1.5" /></div>
            <div><Label className="text-sm">Köşe Yuvarlaklığı</Label><Input value={s.radius || "0.75rem"} onChange={(e) => set("radius", e.target.value)} className="mt-1.5" /></div>
          </div>
          <div>
            <Label className="text-sm">Aktif Coinler</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {COINS.map((c) => (
                <button key={c} onClick={() => toggleCoin(c)} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium ${(s.active_coins || []).includes(c) ? "border-primary bg-primary text-white" : "border-border"}`}>
                  <CoinIcon symbol={c} size={16} /> {c}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2.5 pt-2">
            {[["show_qr", "QR Kod göster"], ["show_email", "E-posta göster"], ["show_order_id", "Sipariş No göster"], ["show_description", "Açıklama göster"], ["show_countdown", "Geri sayım göster"]].map(([k, l]) => (
              <div key={k} className="flex items-center justify-between">
                <span className="text-sm">{l}</span>
                <Switch checked={!!s[k]} onCheckedChange={(v) => set(k, v)} />
              </div>
            ))}
          </div>
          <div><Label className="text-sm">Başarılı Ödeme URL</Label><Input value={s.success_url || ""} onChange={(e) => set("success_url", e.target.value)} className="mt-1.5" placeholder="https://..." /></div>
        </Card>

        {/* Live preview */}
        <div>
          <p className="text-sm font-semibold mb-3 text-muted-foreground">Canlı Önizleme</p>
          <div className="rounded-2xl border border-border p-6" style={{ background: s.background || "#fff" }}>
            <div className="mx-auto max-w-xs rounded-2xl border border-border bg-white p-5 shadow-sm" data-testid="checkout-preview">
              <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
                {s.logo_url ? <img src={s.logo_url} alt="" className="h-9 w-9 rounded-lg object-cover" /> : <div className="h-9 w-9 rounded-lg" style={{ background: s.primary_color || "#0066FF", opacity: 0.15 }} />}
                <span className="text-sm font-bold text-slate-900">{s.store_name || "Mağazanız"}</span>
              </div>
              <div className="py-4 text-center">
                <p className="text-xs text-slate-400">Toplam Tutar</p>
                <p className="text-2xl font-extrabold tracking-tighter text-slate-900">{formatTRY(1000)}</p>
              </div>
              <div className="flex justify-center gap-2 pb-4">
                {(s.active_coins || []).slice(0, 5).map((c) => <CoinIcon key={c} symbol={c} size={24} />)}
              </div>
              <button className="w-full py-2.5 text-sm font-semibold text-white" style={{ background: s.button_color || "#0066FF", borderRadius: s.radius || "0.75rem" }}>Ödemeye Devam Et</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
